import React, { useEffect, useState, useCallback } from 'react';
import SearchFilterBar from '../components/SearchFilterBar';
import Pagination from '../components/Pagination';
import ProductForm from '../components/ProductForm';
import { listProducts, createProduct, updateProduct, deleteProduct } from '../salesKitAPI';

export default function ProductsPage({ onBack }) {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const data = await listProducts({ search, page, limit: 10 });
      setItems(data.items);
      setPagination({ page: data.pagination.page, totalPages: data.pagination.totalPages });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSave = async (form) => {
    if (editing && editing !== 'new') await updateProduct(editing._id, form);
    else await createProduct(form);
    setEditing(null);
    load(pagination.page);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await deleteProduct(id);
    load(pagination.page);
  };

  return (
    <div className="min-h-screen bg-[#0B0B12] text-white px-4 py-6 sm:px-8 sm:py-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-sm text-white/50 hover:text-white transition">← Back</button>
          <h2 className="text-xl font-bold tracking-tight">Products</h2>
          <span className="w-12" />
        </div>

        <SearchFilterBar search={search} onSearchChange={setSearch} onAddClick={() => setEditing('new')} addLabel="Add Product" />

        {error && <p className="text-rose-400 text-sm mb-3">{error}</p>}

        {loading ? (
          <p className="text-white/40 text-sm">Loading…</p>
        ) : !items.length ? (
          <div className="text-center py-16 text-white/30 rounded-3xl ring-1 ring-white/5 bg-white/[0.02]">
            No products yet — hit "+ Add Product" to build your catalogue.
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto rounded-2xl ring-1 ring-white/10 bg-[#14141C]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-white/40 border-b border-white/5">
                    <th className="px-4 py-3 font-semibold">Product</th>
                    <th className="px-4 py-3 font-semibold">Category</th>
                    <th className="px-4 py-3 font-semibold">Price</th>
                    <th className="px-4 py-3 font-semibold">MOQ</th>
                    <th className="px-4 py-3 font-semibold">Lead Time</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p._id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition">
                      <td className="px-4 py-3 font-medium">{p.productName}</td>
                      <td className="px-4 py-3 text-white/60">{p.category || '—'}</td>
                      <td className="px-4 py-3 text-emerald-400 font-semibold">{p.Price ? `₹${p.Price}` : '—'}</td>
                      <td className="px-4 py-3 text-white/60">{p.MOQ || '—'}</td>
                      <td className="px-4 py-3 text-white/60">{p.LeadTime || '—'}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => setEditing(p)} className="text-violet-400 hover:text-violet-300 text-xs font-semibold mr-3">Edit</button>
                        <button onClick={() => handleDelete(p._id)} className="text-rose-400 hover:text-rose-300 text-xs font-semibold">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {items.map((p) => (
                <div key={p._id} className="rounded-2xl bg-[#14141C] ring-1 ring-white/10 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold">{p.productName}</p>
                    <span className="text-emerald-400 font-semibold text-sm shrink-0">{p.Price ? `₹${p.Price}` : '—'}</span>
                  </div>
                  <p className="text-xs text-white/40 mt-1">{p.category || 'Uncategorised'}</p>

                  <div className="flex gap-4 mt-3 text-xs text-white/50">
                    <span>MOQ: {p.MOQ || '—'}</span>
                    <span>Lead: {p.LeadTime || '—'}</span>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setEditing(p)} className="flex-1 py-2 rounded-xl bg-white/5 ring-1 ring-white/10 text-xs font-semibold text-white/80">Edit</button>
                    <button onClick={() => handleDelete(p._id)} className="flex-1 py-2 rounded-xl bg-rose-500/10 ring-1 ring-rose-500/30 text-xs font-semibold text-rose-400">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <Pagination page={pagination.page} totalPages={pagination.totalPages} onChange={load} />

        {editing && (
          <ProductForm initial={editing === 'new' ? null : editing} onSave={handleSave} onClose={() => setEditing(null)} />
        )}
      </div>
    </div>
  );
}
